import { normalizeAuditsToArray } from "../utils/normalize-group-audits.js";
import { hasReadableAuditValue, resolveUpdateAuditValueSource } from "./audit-value-readability.js";
import { normalizeAuditOperationToken, normalizeFieldToken } from "./audit-token-normalizers.js";

export function shouldIgnoreAuditField(field) {
  const normalized = normalizeFieldToken(field);
  return (
    normalized === "id_client" ||
    normalized === "idclient" ||
    normalized === "id_cliente"
  );
}

/**
 * Histórico (aprovado/reprovado) às vezes omite `operation`; inferir criação pelo mesmo critério de `toChangeRecord` (valor / to sem "de").
 * @param {Record<string, unknown>} audit
 * @returns {'criacao' | null}
 */
function inferCreationKindFromAuditPayload(audit) {
  if (
    Array.isArray(audit?.createdFields) &&
    audit.createdFields.length > 0 &&
    !(Array.isArray(audit?.deletedFields) && audit.deletedFields.length > 0)
  ) {
    return "criacao";
  }
  if (Array.isArray(audit?.deletedFields) && audit.deletedFields.length > 0) {
    return null;
  }
  const fromSrc = resolveUpdateAuditValueSource(audit, "from");
  if (hasReadableAuditValue(fromSrc)) {
    return null;
  }
  const explicitCreate = [audit?.value, audit?.valor, audit?.newValue, audit?.new_value].some((v) =>
    hasReadableAuditValue(v),
  );
  if (explicitCreate) {
    return "criacao";
  }
  const toSrc = resolveUpdateAuditValueSource(audit, "to");
  if (hasReadableAuditValue(toSrc)) {
    return "criacao";
  }
  return null;
}

/** @returns {'criacao' | 'atualizacao' | 'delecao'} */
export function resolveAuditOperationKind(audit) {
  const candidates = [
    audit?.operation,
    audit?.operationType,
    audit?.tipo_operacao,
    audit?.tipoOperacao,
    audit?.operation_code,
    audit?.operationCode,
    audit?.tipo,
    audit?.type,
    audit?.action,
    audit?.acao,
  ];
  for (const raw of candidates) {
    const t = normalizeAuditOperationToken(raw);
    if (!t || t === "null" || t === "undefined") {
      continue;
    }
    if (
      t === "delecao" ||
      t === "delete" ||
      t === "deletion" ||
      t === "remove" ||
      t === "removed"
    ) {
      return "delecao";
    }
    if (
      t === "criacao" ||
      t === "create" ||
      t === "creation" ||
      t === "insert" ||
      t === "created" ||
      t === "novo" ||
      t === "nova" ||
      t === "add" ||
      t === "added" ||
      t === "inclusao" ||
      t === "include" ||
      t === "included"
    ) {
      return "criacao";
    }
    if (
      t === "atualizacao" ||
      t === "update" ||
      t === "updated" ||
      t === "alteracao" ||
      t === "modified" ||
      t === "edit" ||
      t === "edited"
    ) {
      return "atualizacao";
    }
  }
  if (Array.isArray(audit?.deletedFields) && audit.deletedFields.length > 0) {
    return "delecao";
  }
  const inferredCreation = inferCreationKindFromAuditPayload(audit);
  if (inferredCreation) {
    return inferredCreation;
  }
  return "atualizacao";
}

export function isAuditEventVisible(audit) {
  if (resolveAuditOperationKind(audit) === "delecao") {
    return true;
  }
  return !shouldIgnoreAuditField(audit?.field);
}

export function hasVisibleAuditChanges(group) {
  const audits = normalizeAuditsToArray(group?.audits);
  return audits.some(isAuditEventVisible);
}
